import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { LeadSubmission, QRCodeVersion, QRDesignTemplate, QRFolder, QRUTMTemplate, UTMSettings } from '../types';

const requireUserId = () => {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error('Du musst angemeldet sein.');
  return userId;
};

export class WorkspaceService {
  static async getFolders(): Promise<QRFolder[]> {
    const ownerId = requireUserId();
    const snapshot = await getDocs(query(collection(db, 'qr-folders'), where('ownerId', '==', ownerId)));
    return snapshot.docs.map(item => ({
      id: item.id,
      ownerId,
      name: item.data().name || 'Ohne Namen',
      color: item.data().color || '#2563eb',
      createdAt: item.data().createdAt?.toDate?.() || new Date()
    })).sort((a, b) => a.name.localeCompare(b.name));
  }

  static async createFolder(name: string, color = '#2563eb'): Promise<string> {
    const ownerId = requireUserId();
    const reference = await addDoc(collection(db, 'qr-folders'), { ownerId, name: name.trim(), color, createdAt: serverTimestamp() });
    return reference.id;
  }

  static async updateFolder(id: string, updates: Pick<QRFolder, 'name' | 'color'>): Promise<void> {
    await updateDoc(doc(db, 'qr-folders', id), { name: updates.name.trim(), color: updates.color });
  }

  static async deleteFolder(id: string): Promise<void> {
    await deleteDoc(doc(db, 'qr-folders', id));
  }

  static async getTemplates(): Promise<QRDesignTemplate[]> {
    const ownerId = requireUserId();
    const snapshot = await getDocs(query(collection(db, 'qr-templates'), where('ownerId', '==', ownerId)));
    return snapshot.docs.map(item => ({
      id: item.id,
      ownerId,
      name: item.data().name || 'Vorlage',
      styleOptions: item.data().styleOptions || {},
      createdAt: item.data().createdAt?.toDate?.() || new Date()
    })).sort((a, b) => a.name.localeCompare(b.name));
  }

  static async saveTemplate(name: string, styleOptions: QRDesignTemplate['styleOptions']): Promise<string> {
    const ownerId = requireUserId();
    const reference = doc(collection(db, 'qr-templates'));
    await setDoc(reference, { ownerId, name: name.trim(), styleOptions, createdAt: serverTimestamp() });
    return reference.id;
  }

  static async deleteTemplate(id: string): Promise<void> {
    await deleteDoc(doc(db, 'qr-templates', id));
  }

  static async getUTMTemplates(): Promise<QRUTMTemplate[]> {
    const ownerId = requireUserId();
    const snapshot = await getDocs(query(collection(db, 'qr-utm-presets'), where('ownerId', '==', ownerId)));
    return snapshot.docs.map(item => ({ id: item.id, ownerId, name: item.data().name || 'UTM-Vorlage', utm: item.data().utm || {}, createdAt: item.data().createdAt?.toDate?.() || new Date() })).sort((a,b) => a.name.localeCompare(b.name));
  }

  static async saveUTMTemplate(name: string, utm: UTMSettings): Promise<string> {
    const ownerId = requireUserId();
    const reference = doc(collection(db, 'qr-utm-presets'));
    await setDoc(reference, { ownerId, name: name.trim(), utm, createdAt: serverTimestamp() });
    return reference.id;
  }

  static async getVersions(qrId: string): Promise<QRCodeVersion[]> {
    requireUserId();
    const snapshot = await getDocs(query(collection(db, 'qr-codes', qrId, 'versions'), orderBy('changedAt', 'desc'), limit(50)));
    return snapshot.docs.map(item => ({
      id: item.id,
      qrId,
      ownerId: item.data().ownerId,
      previousTarget: item.data().previousTarget || '',
      newTarget: item.data().newTarget || '',
      changedAt: item.data().changedAt?.toDate?.() || new Date(),
      changedBy: item.data().changedBy || item.data().ownerId,
      comment: item.data().comment || '',
      snapshot: item.data().snapshot || {}
    }));
  }

  static async getLeads(qrId: string): Promise<LeadSubmission[]> {
    requireUserId();
    const snapshot = await getDocs(query(collection(db, 'qr-codes', qrId, 'leads'), orderBy('createdAt', 'desc'), limit(500)));
    return snapshot.docs.map(item => ({
      id: item.id,
      qrId,
      createdAt: item.data().createdAt?.toDate?.() || new Date(),
      name: item.data().name,
      email: item.data().email,
      phone: item.data().phone,
      message: item.data().message
    }));
  }
}
